// components/NotificationBell.jsx
import React, { useState, useEffect, useRef } from 'react';
import { FiBell, FiCheck, FiTrash2, FiInfo, FiCheckCircle, FiAlertTriangle, FiAlertCircle } from 'react-icons/fi';
import notificationService from '../services/notificationService';

const NotificationBell = () => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);

  // Test bildirimleri oluştur (Development modda)
  const createTestNotifications = async () => {
    const testData = [
      { title: 'Hoş Geldiniz!', message: 'Bildirim sistemi aktif.', type: 'success', module: 'system' },
      { title: 'Yeni Öğrenci', message: 'Test öğrenci eklendi.', type: 'info', module: 'ogrenci' },
      { title: 'Ödeme Alındı', message: '500 TL ödeme alındı.', type: 'success', module: 'odeme' },
      { title: 'Uyarı', message: 'Düşük bakiye uyarısı.', type: 'warning', module: 'ogrenci' },
      { title: 'Hata', message: 'Email gönderilemedi.', type: 'error', module: 'email' }
    ];

    for (const data of testData) {
      try {
        await notificationService.createNotification(data);
      } catch (error) {
        console.error('Test bildirim oluşturulamadı:', error);
      }
    }
    
    // Bildirimleri yenile
    fetchNotifications();
  };

  // Bildirimleri yükle
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      
      // Debug: Role kontrolü
      const currentRole = localStorage.getItem('adminRole'); // DÜZELTME: 'adminRole' key'ini kullan
      console.log('🔴 NotificationBell - Current Role:', currentRole);
      console.log('🔴 NotificationBell - Is SuperAdmin:', currentRole === 'superadmin' || currentRole === 'super_admin');
      
      const response = await notificationService.getNotifications({ limit: 20 });
      setNotifications(response.data || []);
      setUnreadCount(response.unreadCount || 0);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  // Component mount olduğunda bildirimleri yükle
  useEffect(() => {
    fetchNotifications();
    
    // Her 30 saniyede bir bildirimleri kontrol et
    const interval = setInterval(fetchNotifications, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Dropdown dışına tıklandığında kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Bildirimi okundu olarak işaretle
  const handleMarkAsRead = async (id) => {
    try {
      await notificationService.markAsRead(id);
      setNotifications(notifications.map(n => 
        n.id === id ? { ...n, is_read: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Tümünü okundu olarak işaretle
  const handleMarkAllAsRead = async () => {
    try {
      await notificationService.markAllAsRead();
      setNotifications(notifications.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Bildirimi sil
  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await notificationService.deleteNotification(id);
      setNotifications(notifications.filter(n => n.id !== id));
      const deletedNotification = notifications.find(n => n.id === id);
      if (!deletedNotification?.is_read) {
        setUnreadCount(Math.max(0, unreadCount - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  // Tip ikonunu getir
  const getTypeIcon = (type) => {
    switch (type) {
      case 'success':
        return <FiCheckCircle className="text-green-500" />;
      case 'warning':
        return <FiAlertTriangle className="text-yellow-500" />;
      case 'error':
        return <FiAlertCircle className="text-red-500" />;
      default:
        return <FiInfo className="text-blue-500" />;
    }
  };

  // Modül isimlerini Türkçeleştir
  const getModuleName = (module) => {
    const modules = {
      'ogrenci': 'Öğrenci',
      'ogretmen': 'Öğretmen',
      'ders': 'Ders',
      'odeme': 'Ödeme',
      'kurssatis': 'Kurs Satış',
      'system': 'Sistem',
      'email': 'E-posta',
      'sms': 'SMS'
    };
    return modules[module] || module;
  };

  // Tarih formatla
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Şimdi';
    if (minutes < 60) return `${minutes} dk önce`;
    if (hours < 24) return `${hours} saat önce`;
    if (days < 7) return `${days} gün önce`;
    
    return date.toLocaleDateString('tr-TR');
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bildirim Zili */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="relative p-2 text-gray-600 hover:text-gray-900 transition-colors"
      >
        <FiBell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div className="absolute right-0 mt-2 w-96 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
          {/* Header */}
          <div className="p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Bildirimler</h3>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllAsRead}
                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
              >
                <FiCheck className="mr-1" />
                Tümünü Okundu İşaretle
              </button>
            )}
          </div>

          {/* Bildirim Listesi */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                <p className="mt-2 text-sm text-gray-500">Yükleniyor...</p>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <FiBell className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">Henüz bildirim yok</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => !notification.is_read && handleMarkAsRead(notification.id)}
                  className={`p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      {getTypeIcon(notification.type)}
                    </div>
                    <div className="ml-3 flex-1">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900">
                            {notification.title}
                          </p>
                          <p className="text-sm text-gray-600 mt-1">
                            {notification.message}
                          </p>
                          <div className="flex items-center mt-2 text-xs text-gray-500">
                            <span className="bg-gray-200 px-2 py-1 rounded">
                              {getModuleName(notification.module)}
                            </span>
                            <span className="ml-2">
                              {formatDate(notification.created_at)}
                            </span>
                          </div>
                        </div>
                        {(localStorage.getItem('adminRole') === 'superadmin' || localStorage.getItem('adminRole') === 'super_admin') && (
                          <button
                            onClick={(e) => handleDelete(notification.id, e)}
                            className="ml-2 text-gray-400 hover:text-red-500"
                          >
                            <FiTrash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer - Sadece Süper Admin için */}
          {notifications.length > 0 && (localStorage.getItem('adminRole') === 'superadmin' || localStorage.getItem('adminRole') === 'super_admin') && (
            <div className="p-3 border-t border-gray-200 text-center">
              <a
                href="/admin/notifications"
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Tüm Bildirimleri Görüntüle
              </a>
            </div>
          )}
          
          {/* Development Mode - Test Button */}
          {process.env.NODE_ENV === 'development' && notifications.length === 0 && (
            <div className="p-3 border-t border-gray-200 text-center">
              <button
                onClick={createTestNotifications}
                className="text-sm bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
              >
                Test Bildirimleri Oluştur
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
